import { useState, useRef, useEffect, KeyboardEvent } from "react";
import C from "classnames";

import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";

import { useSandbox, useProxyIntercept } from "../../../contexts/SandboxHooks";
import {
  parseSandboxAppMessage,
  sendSandboxAppCommand,
} from "../../../contexts/SandboxAppMessages";

import S from "./_styles.module.scss";

interface SandboxBrowserProps {
  app: { name?: string; navigation?: boolean };
}

const SandboxBrowser = ({ app }: SandboxBrowserProps) => {
  // Call hooks to access sandbox and proxy state
  const {
    session: { sandbox },
  } = useSandbox();
  const { isIntercepting, pendingRequestText, setInterceptState, resumeInterceptedRequest } =
    useProxyIntercept();

  // Sandbox will have multiple apps; get the one from the sandbox status that matches the app name on the tab
  const thisApp = sandbox?.apps?.[app.name ?? ""] || {};

  // Does this app support navigation?
  const supportsNavigation = app.navigation || false;

  // Set the default URL for the tab
  const defaultUrl = thisApp?.url || "";

  // State for the component
  const [view, setView] = useState("browser"); // "browser," "source," or "proxy
  const [showSource, setShowSource] = useState(false);
  const [inputUrl, setInputUrl] = useState(defaultUrl);
  const [pageSource, setPageSource] = useState("");
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);
  const [editedRequestText, setEditedRequestText] = useState("");
  const browserHistory = useRef({ stack: [defaultUrl], index: 0 });
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Helper method to get the current Url in history, or some other url if index is provided
  const getHistoryUrl = (index?: number) => {
    if (index === undefined) {
      index = browserHistory.current.index;
    }
    return browserHistory.current.stack[index] ?? defaultUrl;
  };

  // When the sandbox is running, set the defaultUrl to the app.url
  useEffect(() => {
    setInputUrl(defaultUrl);
    browserHistory.current = { stack: [defaultUrl], index: 0 };
  }, [defaultUrl]);

  // Sync edited request text when pending request text changes
  useEffect(() => {
    if (pendingRequestText) {
      setEditedRequestText(pendingRequestText);
    }
  }, [pendingRequestText]);

  // Set the view based on showSource flag and intercept state
  useEffect(() => {
    const haveInterceptedRequest = isIntercepting && !!pendingRequestText;
    if (haveInterceptedRequest) {
      setView("proxy");
    } else if (showSource) {
      setView("source");
    } else {
      setView("browser");
    }
  }, [showSource, isIntercepting, pendingRequestText]);

  // Listen for messages from the iframe to update URL and page source
  useEffect(() => {
    const onMessage = (ev: MessageEvent) => {
      // Figure out which child iframe the message originated from, if any
      const eventWindow = (ev.source as Window | null)?.window;
      const iframeElem = iframeRef.current;
      if (!iframeElem || iframeElem.contentWindow !== eventWindow) {
        return;
      }

      // Parse the message data
      const message = parseSandboxAppMessage(ev.data);
      if (!message) {
        return;
      }

      // This message came from a child iframe, and we've identified the index of its
      // state object: parse the message and update that state accordingly
      switch (message.action) {
        case "location":
          {
            const { protocol, host, pathname, search } = message;
            const url = `${protocol}//${host}${pathname}${search}`;
            const history = browserHistory.current;
            if (url !== history.stack[history.index]) {
              updateBrowserState(url);
            }
          }
          break;
        case "source":
          setPageSource(message.data);
          setShowSource(true);
          break;
        default:
          console.log(`Unhandled sandbox app message: ${JSON.stringify(message)}`);
      }
    };
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, []);

  const updateBrowserState = (newUrl: string) => {
    setInputUrl(newUrl);
    setPageSource("");

    // Update browser history
    const oldHistory = browserHistory.current;
    const newHistory = {
      stack: oldHistory.stack.slice(0, oldHistory.index + 1).concat(newUrl),
      index: oldHistory.index + 1,
    };
    browserHistory.current = newHistory;
  };

  const navigateToUrl = (newUrl: string) => {
    if (iframeRef.current) iframeRef.current.src = newUrl;
    updateBrowserState(newUrl);
  };

  // When historyIndex changes, update canGoBack and canGoForward states
  useEffect(() => {
    setCanGoBack(browserHistory.current.index > 0);
    setCanGoForward(browserHistory.current.index < browserHistory.current.stack.length - 1);
  }, [browserHistory.current]);

  const goHome = () => {
    navigateToUrl(defaultUrl);
  };

  const handleNavigate = () => {
    navigateToUrl(inputUrl);
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleNavigate();
    }
  };

  const moveInHistory = (newIndex: number) => {
    const newUrl = getHistoryUrl(newIndex);
    setInputUrl(newUrl);
    if (iframeRef.current) iframeRef.current.src = newUrl;

    const newHistory = { ...browserHistory.current };
    newHistory.index = newIndex;
    browserHistory.current = newHistory;
  };

  const handleBack = () => {
    const newIndex = browserHistory.current.index - 1;
    if (newIndex >= 0) {
      moveInHistory(newIndex);
    } else {
      console.warn("Cannot go back, already at the beginning of history");
    }
  };

  const handleForward = () => {
    const newIndex = browserHistory.current.index + 1;
    if (newIndex < browserHistory.current.stack.length) {
      moveInHistory(newIndex);
    } else {
      console.warn("Cannot go forward, already at the end of history");
    }
  };

  // Handle view source/page toggle
  const handleViewSource = (newShowSource: boolean) => {
    if (newShowSource) {
      // We will toggle showSource when the source is received
      sendSandboxAppCommand(iframeRef, getHistoryUrl(), { action: "source" });
    } else {
      setShowSource(false);
    }
  };

  return (
    <div className="browser-container h-full flex flex-col max-h-full">
      {supportsNavigation && (
        <div className="body-sm flex flex-wrap md:flex-nowrap gap-2 mb-2">
          <div className="browser-nav-buttons flex gap-0 shrink-0">
            <Button
              size="small"
              text={true}
              icon="pi pi-chevron-left"
              data-testid="sandbox-browser-back-button"
              onClick={handleBack}
              disabled={!canGoBack}
              aria-label="Back"
            />
            <Button
              size="small"
              text={true}
              icon="pi pi-chevron-right"
              data-testid="sandbox-browser-forward-button"
              onClick={handleForward}
              disabled={!canGoForward}
              aria-label="Forward"
            />
            <Button
              size="small"
              text={true}
              icon="pi pi-home"
              pt={{ icon: { className: "text-lg" } }}
              data-testid="sandbox-browser-home-button"
              onClick={goHome}
              aria-label="Home"
            />
          </div>
          <InputText
            type="text"
            variant="filled"
            className="p-inputtext-sm flex-1 min-w-32 md:min-w-0"
            value={inputUrl || ""}
            onChange={e => setInputUrl(e.target.value)}
            onKeyUp={handleKeyPress}
            placeholder="Enter URL"
            aria-label="URL input"
            data-testid="sandbox-browser-url-input"
          />
          <Button
            size="small"
            className="go-button shrink-0"
            data-testid="sandbox-browser-go-button"
            onClick={handleNavigate}
            label="Go"
          />
          <Button
            size="small"
            className="view-source shrink-0"
            label={view === "browser" ? "View Source" : "View Page"}
            severity="secondary"
            data-testid="sandbox-browser-view-source-button"
            onClick={() => handleViewSource(!showSource)}
          />
          <div className="flex items-center gap-2 ml-auto body-sm">
            <InputSwitch
              inputId="intercept-requests"
              className="intercept-requests-switch"
              checked={isIntercepting}
              onChange={e => setInterceptState(e.value)}
              pt={{
                root: { role: "presentation", "aria-checked": undefined },
                input: { "data-testid": "sandbox-browser-intercept-switch" },
              }}
            />{" "}
            <label htmlFor="intercept-requests">Intercept Requests</label>
          </div>
        </div>
      )}
      <div
        className={C("browser-content", "h-full", "w-full", "min-h-0", S.frameContainer, {
          hidden: view !== "browser",
        })}
      >
        {/* Sandbox app iframe - displays the running application */}
        <iframe
          ref={iframeRef}
          src={defaultUrl}
          title="Browser Content"
          className="browser-iframe"
          sandbox="allow-same-origin allow-scripts allow-forms allow-modals allow-popups"
          allow="clipboard-write; clipboard-read"
        />
      </div>
      {/* Displays the source code of the current page */}
      <div
        className={C("flex-1", "min-h-0", S.sourceContainer, "atlas-code", {
          hidden: view !== "source",
        })}
      >
        <textarea
          aria-label="Page source code"
          name="source-code-text"
          className="h-full w-full"
          value={pageSource}
          readOnly={true}
        />
      </div>
      {/* Displays the intercepted request text, allows editing */}
      <div
        className={C("flex-1", "min-h-0", S.proxyContainer, {
          hidden: view !== "proxy",
        })}
      >
        <div className={S.proxyHeader}>
          <span>Pending Request (type to edit below)</span>
          <Button
            size="small"
            severity="secondary"
            icon="pi pi-play"
            className="ml-auto"
            label="Submit Request"
            onClick={async () => {
              await resumeInterceptedRequest(editedRequestText);
            }}
          />
        </div>
        <textarea
          aria-label="Intercepted request text editor"
          name="proxy-request-text"
          className={S.proxyRequestText}
          value={editedRequestText}
          onChange={e => setEditedRequestText(e.target.value)}
          readOnly={false}
        />
      </div>
    </div>
  );
};

export default SandboxBrowser;
